import React from 'react';
import { Button, ButtonGroup } from 'react-bootstrap';
import { isConvertible, getUnitPair, formatUnitForDisplay, convert } from '../../../utils/unitConversion';

/**
 * Unit Selector Component - allows switching between unit pairs (kg/g, L/ml)
 * @param {string} unit - Current unit
 * @param {function} onUnitChange - Callback when unit changes (newUnit) => void
 * @param {number} quantity - Current quantity (optional, for auto-conversion)
 * @param {function} onQuantityChange - Callback when quantity changes due to unit conversion (newQuantity) => void
 * @param {string} materialUnit - Original unit from material (optional, for showing both options)
 */
export default function UnitSelector({ 
    unit, 
    onUnitChange, 
    quantity, 
    onQuantityChange,
    materialUnit = null 
}) {
    const normalizedUnit = (unit || '').trim().toLowerCase();
    const isUnitConvertible = isConvertible(normalizedUnit);
    
    if (!isUnitConvertible) {
        // Not convertible, show as text input or select
        return (
            <input
                type="text"
                className="form-control form-control-sm"
                value={formatUnitForDisplay(unit)}
                onChange={(e) => onUnitChange(e.target.value)}
                placeholder="unit"
                style={{ minWidth: 80 }}
            />
        );
    }

    const unitPair = getUnitPair(normalizedUnit);
    if (!unitPair) {
        return (
            <input
                type="text"
                className="form-control form-control-sm"
                value={formatUnitForDisplay(unit)}
                onChange={(e) => onUnitChange(e.target.value)}
                placeholder="unit"
                style={{ minWidth: 80 }}
            />
        );
    }

    const [smallerUnit, largerUnit] = unitPair;
    const currentUnitNormalized = normalizedUnit;
    // Normalize the pair units for comparison (since getUnitPair returns ['ml', 'L'] with mixed case)
    const smallerUnitNormalized = (smallerUnit || '').trim().toLowerCase();
    const largerUnitNormalized = (largerUnit || '').trim().toLowerCase();
    const isSmaller = currentUnitNormalized === smallerUnitNormalized;
    const isLarger = currentUnitNormalized === largerUnitNormalized;

    const handleUnitToggle = (newUnit) => {
        // Normalize both for comparison
        const currentNormalized = normalizedUnit;
        const newNormalized = (newUnit || '').trim().toLowerCase();
        
        // Check if units are actually different (case-insensitive)
        if (currentNormalized === newNormalized) {
            // Units are the same, don't do anything
            return;
        }
        
        // Units are different - call onUnitChange to update the state
        // The parent component will handle the conversion and re-render
        if (onUnitChange && typeof onUnitChange === 'function') {
            // Use the actual unit value (not normalized) to preserve case
            onUnitChange(newUnit);
        }
    };

    return (
        <ButtonGroup size="sm">
            <Button
                variant={isSmaller ? "primary" : "outline-primary"}
                onClick={() => handleUnitToggle(smallerUnit)}
                title={`Switch to ${formatUnitForDisplay(smallerUnit)}`}
            >
                {formatUnitForDisplay(smallerUnit)}
            </Button>
            <Button
                variant={isLarger ? "primary" : "outline-primary"}
                onClick={() => handleUnitToggle(largerUnit)}
                title={`Switch to ${formatUnitForDisplay(largerUnit)}`}
            >
                {formatUnitForDisplay(largerUnit)}
            </Button>
        </ButtonGroup>
    );
}
